import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { GraphQLService } from './graph-ql.service';
import { map } from 'rxjs/operators';
import { MailingAddress, ShopifyError } from '../models';

@Injectable({
  providedIn: 'root',
})
export class AddressesService extends GraphQLService {
  private STORAGE_KEY = 'CUSTOMER_TOKEN';

  constructor() {
    super();
  }

  CustomerGetAddresses(token: string, after: string = '', take = 10, language?: string) {
    const args: any = { first: take };
    if (after) {
      args['after'] = after;
    }

    const query = this.client.query(`@inContext(language: ${language})`, (root: any) => {
      root.add('customer', { args: { customerAccessToken: token } }, (customer: any) => {
        customer.add('id');
        customer.addConnection('addresses', { args: { first: 200 } }, (address: any) => {
          mailingAddress(address, this.client);
        });
      });
    });
    return from(this.client.send(query)).pipe(
      map((a: any) => {
        const errors = a['errors'] || [];
        if (errors.length) {
          throw errors as ShopifyError[];
        }
        return a['data'].customer.addresses;
      })
    ) as Observable<any>;
  }

  CustomerAddressCreate(token: string, address: MailingAddress, language?: string) {
    const mutation = this.client.mutation(`@inContext(language: ${language})`, (root: any) => {
      root.add('customerAddressCreate', { args: { address, customerAccessToken: token } }, (customerAddressCreate: any) => {
        customerAddressCreate.add('customerUserErrors', (userErrors: any) => {
          userErrors.add('message'), userErrors.add('field');
          userErrors.add('code');
        });
        customerAddressCreate.add('customerAddress', (add: any) => {
          mailingAddress(add, this.client);
        });
      });
    });

    return from(this.client.send(mutation)).pipe(
      map((a: any) => {
        const errors = a['data']?.customerAddressCreate?.customerUserErrors || a['errors'] || [];
        if (errors.length) {
          throw errors;
        }

        return a['data']['customerAddressCreate']?.customerAddress;
      })
    ) as Observable<any>;
  }

  CustomerAddressUpdate(token: string, address: MailingAddress, id: string, language?: string) {
    const shopifyId = id;
    const mutation = this.client.mutation(`@inContext(language: ${language})`, (root: any) => {
      root.add('customerAddressUpdate', { args: { address, id: shopifyId, customerAccessToken: token } }, (customerAddressUpdate: any) => {
        customerAddressUpdate.add('customerUserErrors', (userErrors: any) => {
          userErrors.add('message'), userErrors.add('field');
          userErrors.add('code');
        });
        customerAddressUpdate.add('customerAddress', (add: any) => {
          mailingAddress(add, this.client);
        });
      });
    });

    return from(this.client.send(mutation)).pipe(
      map((a: any) => {
        const errors = a['data']?.customerAddressUpdate?.customerUserErrors || a['errors'] || [];
        if (errors.length) {
          throw errors;
        }

        return { address: a['data']['customerAddressUpdate'].customerAddress, id };
      })
    ) as Observable<any>;
  }

  CustomerAddressDelete(token: string, id: string, language?: string) {
    const shopifyId = id;
    const mutation = this.client.mutation(`@inContext(language: ${language})`, (root: any) => {
      root.add('customerAddressDelete', { args: { id: shopifyId, customerAccessToken: token } }, (d: any) => {
        d.add('customerUserErrors', (userErrors: any) => {
          userErrors.add('message'), userErrors.add('field');
          userErrors.add('code');
        });
        d.add('deletedCustomerAddressId');
      });
    });

    return from(this.client.send(mutation)).pipe(
      map((a: any) => {
        const errors = a['data']?.customerAddressDelete?.customerUserErrors || a['errors'] || [];
        if (errors.length) {
          throw errors;
        }

        return a['data'].customerAddressDelete?.['deletedCustomerAddressId'];
      })
    ) as Observable<any>;
  }

  customerDefaultAddressUpdate(token: string, id: string, language?: string) {
    const shopifyId = id;
    const mutation = this.client.mutation(`@inContext(language: ${language})`, (root: any) => {
      root.add('customerDefaultAddressUpdate', { args: { addressId: shopifyId, customerAccessToken: token } }, (d: any) => {
        d.add('customerUserErrors', (userErrors: any) => {
          userErrors.add('message'), userErrors.add('field');
          userErrors.add('code');
        });
        d.add('customer', (c: any) => {
          c.add('defaultAddress', (address: any) => {
            mailingAddress(address, this.client);
          });
        });
      });
    });

    return from(this.client.send(mutation)).pipe(
      map((a: any) => {
        const errors = a['data']?.customerDefaultAddressUpdate?.customerUserErrors || a['errors'] || [];
        if (errors.length) {
          throw errors;
        }

        return a['data']['customerDefaultAddressUpdate'].customer?.defaultAddress;
      })
    ) as Observable<any>;
  }
}

export function mailingAddress(queryBase: any, client: any) {
  queryBase.add('address1');
  queryBase.add('address2');
  queryBase.add('city');
  queryBase.add('company');
  queryBase.add('country');
  queryBase.add('countryCodeV2');
  queryBase.add('firstName');
  queryBase.add('formattedArea');
  queryBase.add('id');
  queryBase.add('lastName');
  queryBase.add('name');
  queryBase.add('phone');
  queryBase.add('province');
  queryBase.add('provinceCode');
  queryBase.add('zip');
}

export function cleanAddressId(id: string) {
  const clean = id.replace('gid://shopify/MailingAddress/', '');

  return clean.substring(0, clean.indexOf('?'));
}
